import React, { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import type { ThemeConfig } from './theme.config'
import { talentMatchTheme } from './theme.config'

// Theme mode types
export type ThemeMode = 'light' | 'dark' | 'auto'

// Branding configuration interface
export interface BrandingConfig {
  organizationName: string
  logoUrl?: string
  primaryColor: string
  supportEmail: string
  customColors?: {
    primary?: string
    secondary?: string
    accent?: string
  }
}

// Theme context interface
interface ThemeContextType {
  theme: ThemeConfig
  mode: ThemeMode
  branding: BrandingConfig
  setMode: (mode: ThemeMode) => void
  updateBranding: (branding: Partial<BrandingConfig>) => void
  isDarkMode: boolean
}

// Default branding configuration
const defaultBranding: BrandingConfig = {
  organizationName: 'Talent Match',
  primaryColor: '#0867ec',
  supportEmail: 'support@talentmatch.com',
}

// Create theme context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Theme provider props
interface ThemeProviderProps {
  children: ReactNode
  initialBranding?: Partial<BrandingConfig>
}

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Theme provider component
export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialBranding = {},
}) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    // Get saved theme mode from localStorage
    const savedMode = localStorage.getItem('talent-match-theme-mode')
    return (savedMode as ThemeMode) || 'auto'
  })

  const [branding, setBranding] = useState<BrandingConfig>(() => {
    // Get saved branding from localStorage or use defaults
    const savedBranding = localStorage.getItem('talent-match-branding')
    const parsedBranding = savedBranding ? JSON.parse(savedBranding) : {}
    return {
      ...defaultBranding,
      ...parsedBranding,
      ...initialBranding,
    }
  })

  const [isDarkMode, setIsDarkMode] = useState<boolean>(false)

  // Determine if dark mode should be active
  useEffect(() => {
    const updateDarkMode = () => {
      if (mode === 'auto') {
        const prefersDark = window.matchMedia(
          '(prefers-color-scheme: dark)'
        ).matches
        setIsDarkMode(prefersDark)
      } else {
        setIsDarkMode(mode === 'dark')
      }
    }

    updateDarkMode()

    // Listen for system theme changes when in auto mode
    if (mode === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      mediaQuery.addEventListener('change', updateDarkMode)
      return () => mediaQuery.removeEventListener('change', updateDarkMode)
    }
  }, [mode])

  // Save theme mode to localStorage
  useEffect(() => {
    localStorage.setItem('talent-match-theme-mode', mode)
  }, [mode])

  // Save branding to localStorage
  useEffect(() => {
    localStorage.setItem('talent-match-branding', JSON.stringify(branding))
  }, [branding])

  // Create customized theme based on branding
  const customizedTheme: ThemeConfig = React.useMemo(() => {
    const theme = { ...talentMatchTheme }

    // Apply custom primary color if provided
    if (branding.customColors?.primary) {
      // Generate color palette from primary color
      const primaryColor = branding.customColors.primary
      theme.colors.primary = {
        ...theme.colors.primary,
        500: primaryColor,
        // You could add a color palette generator here
      }
    }

    return theme
  }, [branding])

  // Update branding configuration
  const updateBranding = (newBranding: Partial<BrandingConfig>) => {
    setBranding(prev => ({ ...prev, ...newBranding }))
  }

  // Apply CSS custom properties for theming
  useEffect(() => {
    const root = document.documentElement

    // Apply theme colors as CSS custom properties
    root.style.setProperty('--tm-primary-color', branding.primaryColor)
    root.style.setProperty(
      '--tm-organization-name',
      `"${branding.organizationName}"`
    )

    // Apply dark/light mode class
    root.classList.toggle('dark', isDarkMode)
    root.classList.toggle('light', !isDarkMode)

    // Apply custom colors if provided
    if (branding.customColors) {
      Object.entries(branding.customColors).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--tm-${key}-color`, value)
        }
      })
    }
  }, [branding, isDarkMode])

  const contextValue: ThemeContextType = {
    theme: customizedTheme,
    mode,
    branding,
    setMode,
    updateBranding,
    isDarkMode,
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  )
}

// Hook to get current branding configuration
export const useBranding = () => {
  const { branding, updateBranding } = useTheme()
  return { branding, updateBranding }
}

// Hook to get theme mode controls
export const useThemeMode = () => {
  const { mode, setMode, isDarkMode } = useTheme()
  return { mode, setMode, isDarkMode }
}
